import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Wallet,
  Briefcase,
  Wrench,
  ArrowRight,
  Clock,
  BookOpen,
  Calendar,
} from "lucide-react";
import {
  CATEGORIES,
  POSTS,
  getFeaturedPosts,
  getPostsByCategory,
  formatDate,
} from "@/data/logbookPosts";
import { LINKS } from "@/config/links";

const ICON_MAP = { Wallet, Briefcase, Wrench };

export default function Logbook() {
  const [params, setParams] = useSearchParams();
  const activeCat = params.get("category");

  const featured = getFeaturedPosts();
  const visiblePosts = activeCat
    ? getPostsByCategory(activeCat)
    : [...POSTS].sort((a, b) => (a.date < b.date ? 1 : -1));

  const activeCatMeta = activeCat ? CATEGORIES[activeCat] : null;

  return (
    <div data-testid="page-logbook">
      {/* HERO */}
      <section className="relative pt-20 sm:pt-28 pb-12 sm:pb-16 overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-[460px] gradient-radial-blue pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm">
              <BookOpen className="h-3.5 w-3.5 text-[#FFCB05]" />
              <span className="text-xs font-semibold tracking-[0.18em] uppercase text-white/80">Linehaul Books · The Logbook</span>
            </div>

            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-white leading-[1.05]">
              The <span className="text-[#FFCB05]">Logbook</span>.
            </h1>
            <p className="mt-5 max-w-2xl text-base sm:text-lg text-[#9CA3AF] leading-relaxed">
              Field notes on the finance, business, and operations of trucking — from the firm that keeps your books. Short reads. Real numbers. Zero fluff.
            </p>
          </motion.div>

          {/* CATEGORY CARDS */}
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
            {Object.values(CATEGORIES).map((cat, i) => {
              const Icon = ICON_MAP[cat.icon] || BookOpen;
              const isActive = activeCat === cat.key;
              return (
                <motion.button
                  key={cat.key}
                  type="button"
                  onClick={() => {
                    if (isActive) {
                      params.delete("category");
                      setParams(params, { replace: true });
                    } else {
                      setParams({ category: cat.key }, { replace: true });
                    }
                    window.scrollTo({ top: 520, behavior: "smooth" });
                  }}
                  data-testid={`logbook-category-${cat.key}`}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className={`group relative text-left p-7 rounded-2xl border transition-all duration-300 ${
                    isActive
                      ? "bg-gradient-to-b from-[#00274C] to-[#040914] border-[#FFCB05]/50 maize-glow"
                      : "bg-gradient-to-b from-[#00274C]/40 to-[#040914] border-white/5 hover:border-[#FFCB05]/30 hover:-translate-y-1"
                  }`}
                >
                  <span className="inline-grid place-items-center h-11 w-11 rounded-xl bg-[#040914] border border-white/10 group-hover:border-[#FFCB05]/40 transition-colors">
                    <Icon className="h-5 w-5 text-[#FFCB05]" />
                  </span>
                  <h3 className="mt-5 font-display text-xl font-semibold text-white">{cat.name}</h3>
                  <p className="mt-2 text-sm text-[#FFCB05]/90">{cat.tagline}</p>
                  <p className="mt-3 text-sm text-[#9CA3AF] leading-relaxed">{cat.description}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-[#FFCB05]">
                    {isActive ? "Showing this category" : "Browse"} <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </motion.button>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURED — only when no category filter */}
      {!activeCat && (
        <section className="relative py-12 sm:py-16">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="flex items-end justify-between gap-4 mb-8">
              <div>
                <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#FFCB05]">Featured</p>
                <h2 className="mt-2 font-display text-2xl sm:text-3xl font-medium tracking-tight text-white">Pinned to the dashboard.</h2>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {featured.map((post) => (
                <FeaturedCard key={post.slug} post={post} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* POSTS LIST */}
      <section className="relative py-12 sm:py-16 bg-[#0A111E] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
            <div>
              <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#FFCB05]">
                {activeCatMeta ? activeCatMeta.name : "Recent"}
              </p>
              <h2 className="mt-2 font-display text-2xl sm:text-3xl font-medium tracking-tight text-white">
                {activeCatMeta ? activeCatMeta.description : "Latest entries from the road."}
              </h2>
            </div>
            {activeCat && (
              <button
                type="button"
                onClick={() => { params.delete("category"); setParams(params, { replace: true }); }}
                data-testid="logbook-clear-filter"
                className="self-start sm:self-auto px-4 py-2 rounded-full text-xs font-semibold tracking-[0.14em] uppercase border border-white/10 text-[#9CA3AF] hover:text-white hover:border-[#FFCB05]/40 transition-colors"
              >
                Clear filter
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {visiblePosts.map((post, i) => (
              <PostCard key={post.slug} post={post} index={i} />
            ))}
          </div>

          {visiblePosts.length === 0 && (
            <p className="text-center text-[#9CA3AF] py-12">No entries in this category yet — check back soon.</p>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 sm:py-24">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#00274C] via-[#0A111E] to-[#040914] p-10 sm:p-14">
            <div className="absolute -top-24 -right-20 h-72 w-72 rounded-full bg-[#FFCB05]/15 blur-3xl" />
            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              <div>
                <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#FFCB05]">Need this work done — not just read about?</p>
                <h3 className="mt-3 font-display text-3xl sm:text-4xl font-medium tracking-tight text-white">See if Linehaul is a fit.</h3>
              </div>
              <div className="flex flex-wrap gap-3 md:justify-end">
                <Link
                  to="/services"
                  data-testid="logbook-cta-pricing-btn"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold bg-[#FFCB05] text-[#040914] hover:bg-[#FFD633] transition-all hover:-translate-y-0.5"
                >
                  See Pricing <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href={LINKS.discoveryCall}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="logbook-cta-calendly-btn"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold border border-white/15 text-white hover:border-[#FFCB05]/50 hover:bg-white/5 transition-all"
                >
                  See if Linehaul is a fit <Calendar className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function FeaturedCard({ post }) {
  const cat = CATEGORIES[post.category];
  return (
    <Link
      to={`/logbook/${post.slug}`}
      data-testid={`logbook-featured-${post.slug}`}
      className="group relative flex flex-col p-8 sm:p-10 rounded-2xl bg-gradient-to-b from-[#00274C]/50 to-[#040914] border border-white/5 hover:border-[#FFCB05]/40 hover:-translate-y-1 transition-all duration-300"
    >
      <div className="flex items-center gap-3">
        <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold tracking-[0.18em] uppercase bg-[#FFCB05] text-[#040914]">
          Featured · {cat.name}
        </span>
      </div>
      <h3 className="mt-5 font-display text-xl sm:text-2xl font-semibold text-white leading-snug group-hover:text-[#FFCB05] transition-colors">
        {post.title}
      </h3>
      <p className="mt-3 text-sm sm:text-base text-[#9CA3AF] leading-relaxed">{post.excerpt}</p>
      <div className="mt-6 flex items-center gap-4 text-xs text-[#6B7280]">
        <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {post.readMinutes} min read</span>
        <span>·</span>
        <span>{formatDate(post.date)}</span>
      </div>
    </Link>
  );
}

function PostCard({ post, index }) {
  const cat = CATEGORIES[post.category];
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.5, delay: index * 0.04 }}
    >
      <Link
        to={`/logbook/${post.slug}`}
        data-testid={`logbook-post-${post.slug}`}
        className="group flex flex-col h-full p-7 rounded-2xl bg-[#040914] border border-white/5 hover:border-[#FFCB05]/30 hover:-translate-y-1 transition-all duration-300"
      >
        <span className="self-start px-2.5 py-1 rounded-full text-[10px] font-semibold tracking-[0.18em] uppercase border border-[#FFCB05]/30 text-[#FFCB05]">
          {cat.name}
        </span>
        <h3 className="mt-5 font-display text-lg font-semibold text-white leading-snug group-hover:text-[#FFCB05] transition-colors">
          {post.title}
        </h3>
        <p className="mt-2.5 text-sm text-[#9CA3AF] leading-relaxed line-clamp-3">{post.excerpt}</p>
        <div className="mt-auto pt-6 flex items-center justify-between text-xs text-[#6B7280]">
          <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {post.readMinutes} min</span>
          <span>{formatDate(post.date)}</span>
        </div>
      </Link>
    </motion.div>
  );
}
